const EN_US_FORMATTER = new Intl.DateTimeFormat('en-US', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const EN_US_LONG_FORMATTER = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/**
 * Formats a short ISO 8601 date (YYYY-MM-DD) into its en-US locale counterpart (MM/DD/YYYY)
 *
 * @param date ISO 8601 date string with no time or timezone
 */
export const formatShortIsoDate = (date: string = ''): string => {
  try {
    // Split `date` into parts so the Date constructor won't default to UTC.
    const [year, month, day] = date.split('-');
    return EN_US_FORMATTER.format(new Date(Number(year), Number(month) - 1, Number(day)));
  } catch (err) {
    console.error(`Error formatting short ISO date '${date}' as en-US date:`, err);
  }

  return '';
};

/**
 * Formats a short ISO 8601 date (YYYY-MM-DD) into its long en-US locale counterpart
 * (e.g. `January 1, 2027`)
 *
 * @param date ISO 8601 date string with no time or timezone
 */
export const formatLongIsoDate = (date: string = ''): string => {
  try {
    // Split `date` into parts so the Date constructor won't default to UTC.
    const [year, month, day] = date.split('-');
    return EN_US_LONG_FORMATTER.format(new Date(Number(year), Number(month) - 1, Number(day)));
  } catch (err) {
    console.error(`Error formatting long ISO date '${date}' as en-US date:`, err);
  }

  return '';
};

/**
 * Returns whether a short ISO 8601 date (YYYY-MM-DD) is strictly after another.
 *
 * @param date ISO 8601 date string with no time or timezone
 * @param dateToCompare ISO 8601 date string with no time or timezone
 */
export const isAfterIsoDate = (date: string, dateToCompare: string): boolean => {
  // Split into parts so the Date constructor won't default to UTC.
  const [year, month, day] = date.split('-');
  const [compareYear, compareMonth, compareDay] = dateToCompare.split('-');

  const left = new Date(Number(year), Number(month) - 1, Number(day));
  const right = new Date(Number(compareYear), Number(compareMonth) - 1, Number(compareDay));

  return left.getTime() > right.getTime();
};

/** Today as a short ISO 8601 date (YYYY-MM-DD) in the local timezone. */
const getTodayIsoDate = (): string => {
  const now = new Date();

  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
};

/**
 * Returns whether a short ISO 8601 date (YYYY-MM-DD) is strictly after today
 * in the local timezone.
 *
 * @param date ISO 8601 date string with no time or timezone
 */
export const isFutureIsoDate = (date: string): boolean => {
  return isAfterIsoDate(date, getTodayIsoDate());
};

/**
 * Returns whether a short ISO 8601 date (YYYY-MM-DD) is strictly before today
 * in the local timezone. A date of today is not past.
 *
 * @param date ISO 8601 date string with no time or timezone
 */
export const isPastIsoDate = (date: string): boolean => {
  return isAfterIsoDate(getTodayIsoDate(), date);
};
